import Image from 'next/image';
import Router from 'next/router';
import { memo, useMemo } from 'react';

import { ArrowRight, Plus } from '@air/next-icons';
import { Button, type ButtonProps } from '@air/primitive-button';
import { DropdownMenu, type DropdownMenuOption, renderDropdownItems } from '@air/primitive-dropdown-menu';

import { Routes } from '~/constants/routes';
import { useCreateWorkspace } from '~/hooks/useCreateWorkspace';
import { useSwitchWorkspace } from '~/hooks/useSwitchWorkspace';
import { useWorkspaces } from '~/swr-hooks/workspaces/useWorkspaces';

type WorkspaceSwitcherButtonProps = Omit<ButtonProps, 'children' | 'suffix' | 'color'> & {
  buttonText?: string;
};

export const WorkspaceSwitcherButton = memo(
  ({ buttonText = 'Switch workspaces', appearance = 'outline', ...restOfProps }: WorkspaceSwitcherButtonProps) => {
    const { data: workspaces = [] } = useWorkspaces();
    const { switchWorkspace } = useSwitchWorkspace();
    const { createWorkspace } = useCreateWorkspace();

    const options = useMemo(() => {
      const dropdownOptions: DropdownMenuOption[] = [
        ...workspaces.map(
          (workspace): DropdownMenuOption => ({
            id: workspace.id,
            label: workspace.name,
            type: 'item',
            prefix: (
              <Image
                alt={workspace.name}
                className="block size-6 rounded"
                height={24}
                src={`${workspace.image}?h=48&w=48&fit=crop`}
                width={24}
                unoptimized
              />
            ),
            onSelect: () => {
              switchWorkspace(workspace);
              Router.push(Routes.home);
            },
          }),
        ),
        { id: 'separator-1', type: 'separator' },
        {
          id: 'create-workspace',
          label: 'Join or create a workspace',
          type: 'item',
          prefix: <Plus className="size-4" />,
          onSelect: createWorkspace,
        },
      ];
      return dropdownOptions;
    }, [workspaces, switchWorkspace, createWorkspace]);

    return (
      <DropdownMenu
        align="start"
        side="right"
        trigger={
          <Button appearance={appearance} color="grey" suffix={<ArrowRight className="size-4" />} {...restOfProps}>
            {buttonText}
          </Button>
        }
      >
        {renderDropdownItems({ options })}
      </DropdownMenu>
    );
  },
);

WorkspaceSwitcherButton.displayName = 'WorkspaceSwitcherButton';
